import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Stack,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { Delete, Save, Assignment, Password, Devices, Edit, Visibility, VisibilityOff, PersonAdd, Dialpad, Phone, Apps } from '@mui/icons-material';
import * as ETVConstants from '../ETVConstants';
import IntercomAssignmentDialog from './IntercomAssignmentDialog';
import MessageDialog from '../tools/MessageDialog';
import TestCallStatusIcon from './TestCallStatusIcon';
import IntercomDTMFDialog from './IntercomDTMFDialog';

class IntercomMgmt extends React.Component {
  state = {
    // data
    users: [],
    devices: [],
    all_devices: [],

    // loading/error
    isLoading: false,
    isLoaded: false,
    error: null,

    // dialogs / info
    window: '',
    info_title: '',
    info_message: '',
    info_type: '',
    spinning: false,

    // add user
    newUserName: '',
    newFriendlyName:'',
    newDeviceId: '',
    addKind: 'user',
    newPassword: '',
    newUserPrefix: 'app:',
    newSipExtension: '',
    newSipDomain: '',
    newSipTransport: 'udp',
    newSipTestExtension: '',
    showNewSipPassword: false,

    // assignment dialog
    selected_device_id: '',

    // edit device SIP settings
    editDeviceUserId: null,
    editDeviceName: '',
    editSipExtension: '',
    editSipDomain: '',
    editSipPassword: '',
    editSipTransport: 'udp',
    editSipTestExtension: '',
    showEditSipPassword: false,
    editSipSaving: false,

    // edit intercom user
    editUserId: null,
    editUserPrefix: 'app:',
    editUserName: '',
    editFriendlyName: '',
    editUserPhone: '',
    editUserPassword: '',
    editUserSipExtension: '',
    editUserSipDomain: '',
    editUserSipPassword: '',
    editUserSipTransport: 'udp',
    showEditUserSipPassword: false,
    editUserSaving: false,

    // change password
    changePwUserId: null,
    changePwUserName: '',
    changePwNew: '',
    changePwConfirm: '',
    changePwSaving: false,
  };

  componentDidMount() {
    this.loadIntercomUsers();
    this.loadAllDevices();
  }

  componentDidUpdate() {
    if (!this.state.isLoading && !this.state.isLoaded) {
      this.loadIntercomUsers();
    }
  }

  // --------------- Environment helpers ---------------
  getEnv() {
    const accessToken = localStorage.getItem('etv.access_token') || '';
    const reqUserID = localStorage.getItem('etv.user_id') || '';
    const baseRaw = ETVConstants.getServerBase?.() || '';
    const base = baseRaw.replace(/\/$/, '');
    return { accessToken, reqUserID, base };
  }

  failIfMixedContent(base) {
    if (
      typeof window !== 'undefined' &&
      window.location?.protocol === 'https:' &&
      base.startsWith('http:')
    ) {
      throw new Error(
        `Mixed content blocked: front-end is HTTPS but server base is HTTP (${base}). Use HTTPS or a same-origin proxy.`
      );
    }
  }

  async safeFetchJson(input, init) {
    try {
      const res = await fetch(input, init);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(
          `HTTP ${res.status} ${res.statusText}${text ? ` � ${text.slice(0, 200)}` : ''}`
        );
      }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) return {};
      return await res.json();
    } catch (err) {
      if (err?.name === 'TypeError' && String(err.message).includes('Failed to fetch')) {
        throw new Error(
          'Failed to fetch (network/CORS/mixed-content). Check server CORS, URL, and HTTPS vs HTTP.'
        );
      }
      throw err;
    }
  }

  // --------------- Validation helpers ---------------
  getEffectivePrefix = () => {
    return this.state.addKind === 'device'
      ? 'app:'
      : (this.state.newUserPrefix || 'app:');
  };

  validateIntercomUserName = (rawValue, prefix, addKind) => {
    const value = (rawValue || '').trim();
    const effectivePrefix = addKind === 'device' ? 'app:' : (prefix || 'app:');

    if (!value) {
      return ETVConstants.trans('user_name_required') || 'User name is required';
    }

    if (effectivePrefix === 'app:') {
      if (/\s/.test(value)) {
        return ETVConstants.trans('user_name_no_spaces') || 'User name must not contain spaces';
      }
      return '';
    }

    if (effectivePrefix === 'tel:') {
      // Twilio expects E.164 format: +, country code, and up to 15 total digits.
      if (!/^\+[1-9]\d{6,14}$/.test(value)) {
        return (
          ETVConstants.trans('invalid_phone_number') ||
          'Phone number must be Twilio/E.164 format, e.g. +6591234567'
        );
      }
      return '';
    }

    if (effectivePrefix === 'sip:') {
      if (/\s/.test(value)) {
        return ETVConstants.trans('invalid_sip_address') || 'SIP address must not contain spaces';
      }

      // user@server / user@server.com / user@192.168.1.10 / user@server.com:5060
      const sipRegex =
        /^[a-zA-Z0-9_.!~*'()%+\-]+@((\d{1,3}\.){3}\d{1,3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+)(?::\d{1,5})?$/;

      if (!sipRegex.test(value)) {
        return (
          ETVConstants.trans('invalid_sip_address') ||
          'SIP address must be valid, e.g. user@server.com'
        );
      }
      return '';
    }

    return '';
  };

  validateIntercomPassword = (rawValue) => {
    const value = (rawValue || '').trim();
    if (!value) {
      return ETVConstants.trans('password_required') || 'Password is required';
    }
    return '';
  };

  // --------------- Device list for dropdown ---------------
  loadAllDevices() {
    this.setState({ isLoading: true });
    const accessToken = localStorage.getItem('etv.access_token') || '';
    const reqUserID = localStorage.getItem('etv.user_id') || '';
    const url =
      ETVConstants.getServerBase() + '/device_mgmt?action=list&owner_id=' + this.props.user_id;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          let sorted = Array.isArray(result.list) ? result.list : [];
          sorted.sort(ETVConstants.compareDevice);
          this.setState({ all_devices: sorted, isLoading: false, isLoaded: true });
        },
        () => {
          this.setState({ all_devices: [], isLoading: false, isLoaded: true });
        }
      );
  }

  // --------------- Intercom users ---------------
  filterUsers(list) {
    const res = [];
    for (const u of list || []) {
      const devId = typeof u.device_id === 'string' ? u.device_id.trim() : '';
      if (!devId) {
        res.push({
          ...u,
          intercom_user_id: u.intercom_user_id != null ? String(u.intercom_user_id) : '',
        });
      }
    }
    return res;
  }

  filterDevices(list) {
    const res = [];
    for (const u of list || []) {
      const devId = typeof u.device_id === 'string' ? u.device_id.trim() : '';
      if (devId) {
        res.push(u);
      }
    }
    return res;
  }

  loadIntercomUsers = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const { base, reqUserID, accessToken } = this.getEnv();
      this.failIfMixedContent(base);

      const listUrl =
        `${base}/intercom_admin?action=list_users` +
        `&parent_id=${encodeURIComponent(reqUserID)}` +
        `&access_token=${encodeURIComponent(accessToken)}` +
        `&user_id=${encodeURIComponent(reqUserID)}`;

      const result = await this.safeFetchJson(listUrl, { method: 'GET' });
      const all = Array.isArray(result?.list) ? result.list : [];

      const users = this.filterUsers(all);
      const devices = this.filterDevices(all);

      this.setState({
        users,
        devices,
        isLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        users: [],
        devices: [],
        isLoaded: true,
        isLoading: false,
        error,
      });
    }
  };

  // --------------- UI helpers ---------------
  showWindow = (id, show) => {
    this.setState({ window: show ? id : '' });
  };

  showInfoMessagePrivate(infoType, title, msg, isSpinning) {
    this.setState({
      window: 'message_div',
      info_type: infoType,
      info_title: title,
      info_message: msg,
      spinning: isSpinning,
    });
  }

  closeInfoMessage() {
    this.setState({
      window: '',
      info_type: '',
      info_title: '',
      info_message: undefined,
      spinning: false,
      waiting_for_reply: false,
      aborted: true,
    });
  }

  showErrorMessage(title, msg) {
    this.showInfoMessagePrivate('error', title, msg, false);
  }

  showSuccessMessage(title, msg) {
    this.showInfoMessagePrivate('success', title, msg, false);
  }

  showInfoMessage(title, msg) {
    this.showInfoMessagePrivate('info', title, msg, false);
  }

  showInfoMessageSpinning(title, msg) {
    this.showInfoMessagePrivate('info', title, msg, true);
  }

  openAddUserDialog = () => {
    this.setState({
      window: 'add_intercom_user_div',
      addKind: 'user',
      newUserName: '',
      newFriendlyName:'',
      newDeviceId: '',
      newPassword: '',
      newUserPrefix: 'app:',
      newSipExtension: '',
      newSipDomain: '',
      newSipTransport: 'udp',
      newSipTestExtension: '',
      showNewSipPassword: false,
    });
  };

  openAddDeviceDialog = () => {
    this.setState({
      window: 'add_intercom_user_div',
      addKind: 'device',
      newUserName: '',
      newFriendlyName: '',
      newDeviceId: '',
      newPassword: '',
      newUserPrefix: 'app:',
      newSipExtension: '',
      newSipDomain: '',
      newSipTransport: 'udp',
      newSipTestExtension: '',
      showNewSipPassword: false,
    });
  };

    openDTMFDialog = () => {
    this.setState({
      window: 'dtmf_dialog_div',
    });
  };

  handleSelectDeviceForNewUser = (event) => {
    const deviceId = event.target.value || '';
    const dev = this.state.all_devices.find((d) => String(d.id) === String(deviceId));
    const deviceLabel = dev?.device_name || dev?.id || '';

    this.setState((prev) => ({
      newDeviceId: deviceId,
      newUserPrefix: 'app:',
      newUserName: prev.newUserName ? prev.newUserName : deviceLabel,
    }));
  };
  parseIntercomAddress = (userName, sipExtension, sipDomain) => {
    const raw = userName || '';
    const prefixes = ['sip:', 'tel:', 'app:'];
    const prefix = prefixes.find((p) => raw.toLowerCase().startsWith(p)) || 'app:';
    const value = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    if (prefix === 'sip:') {
      const at = value.indexOf('@');
      return {
        prefix,
        value,
        sipExtension: sipExtension || (at >= 0 ? value.slice(0, at) : value),
        sipDomain: sipDomain || (at >= 0 ? value.slice(at + 1) : ''),
      };
    }
    return { prefix, value, sipExtension: sipExtension || '', sipDomain: sipDomain || '' };
  };

  openEditIntercomUserDialog = (user) => {
    if (!user) return;
    const parsed = this.parseIntercomAddress(user.user_name || '', user.sip_extension || '', user.sip_domain || '');
    this.setState({
      window: 'edit_intercom_user_div',
      editUserId: user.intercom_user_id,
      editUserPrefix: parsed.prefix,
      editUserName: parsed.prefix === 'sip:' ? '' : parsed.value,
      editFriendlyName: user.friendly_name || '',
      editUserPhone: user.user_phone || '',
      editUserPassword: '',
      editUserSipExtension: parsed.sipExtension,
      editUserSipDomain: parsed.sipDomain,
      editUserSipPassword: user.sip_password || '',
      editUserSipTransport: (user.sip_transport || 'udp').toLowerCase() === 'tcp' ? 'tcp' : 'udp',
      showEditUserSipPassword: false,
      editUserSaving: false,
    });
  };

  saveIntercomUserEdit = () => {
    const prefix = this.state.editUserPrefix || 'app:';
    const isSip = prefix === 'sip:';
    const isTel = prefix === 'tel:';
    const sipExtension = (this.state.editUserSipExtension || '').trim();
    const sipDomain = (this.state.editUserSipDomain || '').trim();
    const sipPassword = (this.state.editUserSipPassword || '').trim();
    const rawName = (this.state.editUserName || '').trim();
    const userNameValue = isSip ? `${sipExtension}@${sipDomain}` : rawName;
    const userNameError = this.validateIntercomUserName(userNameValue, prefix, 'user');
    const sipError = isSip && (!sipExtension || !sipDomain || !sipPassword)
      ? (ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, and password are required')
      : (isSip && (/\s/.test(sipExtension) || /\s/.test(sipDomain))
        ? (ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces')
        : '');

    if (!this.state.editUserId) return;
    if (sipError || userNameError) {
      this.showErrorMessage(ETVConstants.trans('error') || 'Error', sipError || userNameError);
      return;
    }

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';
    const body = new URLSearchParams();
    body.set('action', 'update_user');
    body.set('intercom_user_id', String(this.state.editUserId));
    body.set('user_name', `${prefix}${userNameValue}`);
    body.set('friendly_name', (this.state.editFriendlyName || '').trim());
    body.set('user_phone', isTel ? rawName : (this.state.editUserPhone || '').trim());
    body.set('sip_extension', isSip ? sipExtension : '');
    body.set('sip_domain', isSip ? sipDomain : '');
    body.set('sip_password', isSip ? sipPassword : '');
    body.set('sip_transport', isSip && this.state.editUserSipTransport === 'tcp' ? 'tcp' : 'udp');
    body.set('sip_test_extension', '');
    if (isSip && (this.state.editUserPassword || '').trim()) {
      body.set('password', (this.state.editUserPassword || '').trim());
    }

    this.setState({ editUserSaving: true });
    this.showInfoMessageSpinning(ETVConstants.trans('save') || 'Save', ETVConstants.trans('saving') || 'Saving');

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.result || result?.success === true) {
            this.setState({
              editUserSaving: false,
              window: '',
              editUserId: null,
              editUserPrefix: 'app:',
              editUserName: '',
              editFriendlyName: '',
              editUserPhone: '',
              editUserPassword: '',
              editUserSipExtension: '',
              editUserSipDomain: '',
              editUserSipPassword: '',
              editUserSipTransport: 'udp',
              showEditUserSipPassword: false,
              isLoaded: false,
            });
            return;
          }
          this.setState({ editUserSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', result?.reason || result?.error || 'saving_error');
        },
        (err) => {
          this.setState({ editUserSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', String(err?.message || 'saving_error'));
        }
      );
  };
  openEditDeviceDialog = (device) => {
    if (!device) return;
    this.setState({
      window: 'edit_intercom_device_div',
      editDeviceUserId: device.intercom_user_id,
      editDeviceName: device.user_name || '',
      editSipExtension: device.sip_extension || '',
      editSipDomain: device.sip_domain || '',
      editSipPassword: device.sip_password || '',
      editSipTransport: (device.sip_transport || 'udp').toLowerCase() === 'tcp' ? 'tcp' : 'udp',
      editSipTestExtension: device.sip_test_extension || '',
      showEditSipPassword: false,
      editSipSaving: false,
    });
  };

  saveDeviceSipSettings = () => {
    const sipExtension = (this.state.editSipExtension || '').trim();
    const sipDomain = (this.state.editSipDomain || '').trim();
    const sipPassword = (this.state.editSipPassword || '').trim();
    const sipTransport = (this.state.editSipTransport || 'udp').trim().toLowerCase() === 'tcp' ? 'tcp' : 'udp';
    const sipTestExtension = (this.state.editSipTestExtension || '').trim();

    if (!this.state.editDeviceUserId) return;
    if (!sipExtension || !sipDomain || !sipPassword || !sipTestExtension) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, password, and test extension are required'
      );
      return;
    }
    if (/\s/.test(sipExtension) || /\s/.test(sipDomain) || /\s/.test(sipTestExtension)) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces'
      );
      return;
    }

    this.setState({ editSipSaving: true });
    this.showInfoMessageSpinning(
      ETVConstants.trans('save') || 'Save',
      ETVConstants.trans('saving') || 'Saving'
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';
    const body = new URLSearchParams();
    body.set('action', 'update_sip_settings');
    body.set('intercom_user_id', String(this.state.editDeviceUserId));
    body.set('sip_extension', sipExtension);
    body.set('sip_domain', sipDomain);
    body.set('sip_password', sipPassword);
    body.set('sip_transport', sipTransport);
    body.set('sip_test_extension', sipTestExtension);
    body.set('sip_test_number', sipTestExtension);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.result || result?.success === true) {
            this.setState({
              editSipSaving: false,
              window: '',
              editDeviceUserId: null,
              editDeviceName: '',
              editSipExtension: '',
              editSipDomain: '',
              editSipPassword: '',
              editSipTransport: 'udp',
              editSipTestExtension: '',
              showEditSipPassword: false,
              isLoaded: false,
            });
            return;
          }
          this.setState({ editSipSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', result?.reason || result?.error || 'saving_error');
        },
        (err) => {
          this.setState({ editSipSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', String(err?.message || 'saving_error'));
        }
      );
  };

  // --------------- Create / Delete intercom user ---------------
  saveIntercomUser() {
    const {
      newUserName,
      newFriendlyName,
      newDeviceId,
      newPassword,
      addKind,
      newUserPrefix,
      newSipExtension,
      newSipDomain,
      newSipTransport,
      newSipTestExtension,
    } = this.state;

    const rawName = (newUserName || '').trim();
    const sipExtension = (newSipExtension || '').trim();
    const sipDomain = (newSipDomain || '').trim();
    const sipTransport = (newSipTransport || 'udp').trim().toLowerCase();
    const sipTestExtension = (newSipTestExtension || '').trim();
    const pwd = (newPassword || '').trim();
    const prefix = addKind === 'device' ? 'app:' : (newUserPrefix || 'app:');
    const isSipEntry = addKind === 'user' && prefix === 'sip:';
    const isTelEntry = addKind === 'user' && prefix === 'tel:';
    const needsSipFields = addKind === 'device' || isSipEntry;
    const needsPassword = addKind === 'device' || !isTelEntry;
    const userNameValue = isSipEntry ? `${sipExtension}@${sipDomain}` : rawName;
    const userNameError = this.validateIntercomUserName(userNameValue, prefix, addKind);
    const passwordError = needsPassword ? this.validateIntercomPassword(pwd) : '';
    const sipExtensionError = needsSipFields && !sipExtension
      ? (ETVConstants.trans('sip_extension_required') || 'SIP extension is required')
      : (needsSipFields && /\s/.test(sipExtension)
        ? (ETVConstants.trans('invalid_sip_extension') || 'SIP extension must not contain spaces')
        : '');
    const sipDomainError = needsSipFields && !sipDomain
      ? (ETVConstants.trans('sip_domain_required') || 'SIP domain is required')
      : (needsSipFields && /\s/.test(sipDomain)
        ? (ETVConstants.trans('invalid_sip_domain') || 'SIP domain must not contain spaces')
        : '');
    const sipTestExtensionError = addKind === 'device' && !sipTestExtension
      ? (ETVConstants.trans('sip_test_extension_required') || 'SIP test extension is required')
      : (sipTestExtension && /\s/.test(sipTestExtension)
        ? (ETVConstants.trans('invalid_sip_test_extension') || 'SIP test extension must not contain spaces')
        : '');

    if (sipExtensionError || sipDomainError || sipTestExtensionError) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        sipExtensionError || sipDomainError || sipTestExtensionError
      );
      return;
    }

    if (userNameError) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        userNameError
      );
      return;
    }

    if (passwordError) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        passwordError
      );
      return;
    }

    if (addKind === 'device' && !(newDeviceId || '').trim()) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        ETVConstants.trans('device_required') || 'Device is required'
      );
      return;
    }

    const fullUserName = `${prefix}${userNameValue}`;
    const submittedPassword = isTelEntry ? 'tel-user' : pwd;

    this.showInfoMessageSpinning(
      ETVConstants.trans('save'),
      ETVConstants.trans('saving')
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';

    const body = new URLSearchParams();
    body.set('action', 'add_user');
    body.set('user_name', fullUserName);
    if (addKind === 'user') {
      body.set('friendly_name', (newFriendlyName || '').trim());
    }
    body.set('device_id', addKind === 'device' ? (newDeviceId || '') : '');
    body.set('parent_id', reqUserID);
    body.set('password', submittedPassword);
    if (needsSipFields) {
      body.set('sip_username', sipExtension);
      body.set('sip_extension', sipExtension);
      body.set('sip_domain', sipDomain);
      body.set('sip_password', submittedPassword);
      body.set('sip_transport', sipTransport === 'tcp' ? 'tcp' : 'udp');
      body.set('sip_test_number', sipTestExtension);
      body.set('sip_test_extension', sipTestExtension);
      if (sipTestExtension) {
        body.set('testcall_address', 'sip:' + sipTestExtension + '@' + sipDomain);
      }
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((r) => r.json())
      .then(
        (result) => {
          if (result?.result) {
            this.setState({
              isLoaded: false,
              window: '',
              newUserName: '',
              newFriendlyName:'',
              newPassword: '',
              newDeviceId: '',
              newUserPrefix: 'app:',
              newSipExtension: '',
              newSipDomain: '',
              newSipTransport: 'udp',
              newSipTestExtension: '',
              showNewSipPassword: false,
              addKind: 'user',
            });
            return;
          }

          const msg = result.reason;
          if (msg === 'user_exists') {
            this.showErrorMessage(
              ETVConstants.trans('error'),
              ETVConstants.trans('intercom_exists_please_choose_different_name')
            );
          } else if (msg === 'device_exists') {
            this.showErrorMessage(
              ETVConstants.trans('error'),
              ETVConstants.trans('device_already_linked_to_intercom_user_name')
            );
          } else {
            this.showErrorMessage(
              ETVConstants.trans('error') || 'error',
              msg
            );
          }
        },
        (err) => {
          this.showErrorMessage(
            ETVConstants.trans('error') || 'error',
            String(err?.message || 'saving_error')
          );
        }
      );
  }

  deleteIntercomUser(intercomUserID) {
    if (!intercomUserID) return;

    this.showInfoMessageSpinning(
      ETVConstants.trans('delete'),
      ETVConstants.trans('deleting')
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    let url = ETVConstants.getServerBase() + '/intercom_admin?action=delete_user';
    url += '&intercom_user_id=' + encodeURIComponent(intercomUserID);

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        () => {
          this.setState({
            isLoaded: false,
            window: '',
          });
        },
        () => {
          this.showErrorMessage('delete_error');
        }
      );
  }

  // --------------- Change password ---------------
  openChangePasswordDialog = (user) => {
    this.setState({
      changePwUserId: user.intercom_user_id,
      changePwUserName: user.user_name || '',
      changePwNew: '',
      changePwConfirm: '',
    });
    this.showWindow('change_password_div', true);
  };

  submitChangePassword = () => {
    const pw = (this.state.changePwNew || '').trim();
    const conf = (this.state.changePwConfirm || '').trim();

    if (!this.state.changePwUserId || !pw || pw !== conf) return;

    this.setState({ changePwSaving: true });
    this.showInfoMessageSpinning(
      ETVConstants.trans('change_password'),
      ETVConstants.trans('change_password')
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';

    const body = new URLSearchParams();
    body.set('action', 'change_password');
    body.set('intercom_user_id', String(this.state.changePwUserId));
    body.set('password', pw);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.success === false) {
            this.showErrorMessage('password_change_error', result?.error || '');
            this.setState({ changePwSaving: false });
            return;
          }

          this.showSuccessMessage(
            ETVConstants.trans('success'),
            ETVConstants.trans('password_changed')
          );

          this.setState({
            changePwSaving: false,
            window: '',
            changePwUserId: null,
            changePwUserName: '',
            changePwNew: '',
            changePwConfirm: '',
          });
        },
        () => {
          this.setState({ changePwSaving: false });
          this.showErrorMessage('password_change_error');
        }
      );
  };

  // --------------- Assignment dialog ---------------
  showAssignDialog = (deviceID) => {
    this.setState({
      selected_device_id: deviceID,
      window: 'assign_user_dialog_div',
    });
  };

  printAddress(location) {
    if (!location) return '';
    return location.name;
  }

  // --------------- Render ---------------
  render() {
    const {
      users,
      devices,
      all_devices,
      isLoading,
      error,
      window,
      newUserName,
      newDeviceId,
      selected_device_id,
      addKind,
      newPassword,
      newUserPrefix,
      newSipExtension,
      newSipDomain,
      newSipTransport,
      newSipTestExtension,
    } = this.state;

    const effectivePrefix = addKind === 'device' ? 'app:' : (newUserPrefix || 'app:');
    const isAddingDevice = addKind === 'device';
    const isSipUser = !isAddingDevice && effectivePrefix === 'sip:';
    const isTelUser = !isAddingDevice && effectivePrefix === 'tel:';
    const showSipFields = isAddingDevice || isSipUser;
    const showVisiblePassword = isAddingDevice || (!isTelUser && !isAddingDevice);
    const addDialogTitle = isAddingDevice
      ? (ETVConstants.trans('add_intercom_device') || 'Add intercom device')
      : (ETVConstants.trans('add_intercom_user') || 'Add intercom user');
    const addDialogDesc = isAddingDevice
      ? (ETVConstants.trans('add_intercom_device_desc') || 'Create an intercom device login and link it to an existing screen device.')
      : (ETVConstants.trans('add_intercom_user_desc') || 'Create an intercom user address for app, SIP, or telephone calls.');
    const userNameValidationValue = isSipUser
      ? `${(newSipExtension || '').trim()}@${(newSipDomain || '').trim()}`
      : newUserName;
    const userNameError = this.validateIntercomUserName(userNameValidationValue, effectivePrefix, addKind);
    const passwordError = showVisiblePassword ? this.validateIntercomPassword(newPassword) : '';
    const linkedDeviceIds = new Set((devices || []).map((d) => String(d.device_id || '').trim()).filter(Boolean));
    const availableDevices = (all_devices || []).filter((d) => !linkedDeviceIds.has(String(d.id || '').trim()));
    const sipExtensionError = showSipFields && !(newSipExtension || '').trim()
      ? (ETVConstants.trans('sip_extension_required') || 'SIP extension is required')
      : (showSipFields && /\s/.test((newSipExtension || '').trim())
        ? (ETVConstants.trans('invalid_sip_extension') || 'SIP extension must not contain spaces')
        : '');
    const sipDomainError = showSipFields && !(newSipDomain || '').trim()
      ? (ETVConstants.trans('sip_domain_required') || 'SIP domain is required')
      : (showSipFields && /\s/.test((newSipDomain || '').trim())
        ? (ETVConstants.trans('invalid_sip_domain') || 'SIP domain must not contain spaces')
        : '');
    const sipTestExtensionError = isAddingDevice && !(newSipTestExtension || '').trim()
      ? (ETVConstants.trans('sip_test_extension_required') || 'SIP test extension is required')
      : ((isAddingDevice || isSipUser) && (newSipTestExtension || '').trim() && /\s/.test((newSipTestExtension || '').trim())
        ? (ETVConstants.trans('invalid_sip_test_extension') || 'SIP test extension must not contain spaces')
        : '');

    const canSave =
      !userNameError &&
      !passwordError &&
      !sipExtensionError &&
      !sipDomainError &&
      !sipTestExtensionError &&
      (addKind !== 'device' || !!(newDeviceId || '').trim());

    const pw = (this.state.changePwNew || '').trim();
    const conf = (this.state.changePwConfirm || '').trim();
    const canSavePw =
      !this.state.changePwSaving &&
      !!this.state.changePwUserId &&
      !!pw &&
      pw === conf;

    const editPrefix = this.state.editUserPrefix || 'app:';
    const editIsSip = editPrefix === 'sip:';
    const editIsTel = editPrefix === 'tel:';
    const editSipExtension = (this.state.editUserSipExtension || '').trim();
    const editSipDomain = (this.state.editUserSipDomain || '').trim();
    const editSipPassword = (this.state.editUserSipPassword || '').trim();
    const editRawName = (this.state.editUserName || '').trim();
    const editUserNameValue = editIsSip ? `${editSipExtension}@${editSipDomain}` : editRawName;
    const editUserNameError = this.validateIntercomUserName(editUserNameValue, editPrefix, 'user');
    const editSipError = editIsSip && (!editSipExtension || !editSipDomain || !editSipPassword)
      ? (ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, and password are required')
      : (editIsSip && (/\s/.test(editSipExtension) || /\s/.test(editSipDomain))
        ? (ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces')
        : '');
    const canSaveEditUser =
      !this.state.editUserSaving &&
      !!this.state.editUserId &&
      !editUserNameError &&
      !editSipError;

    const getIntercomType = (row) => {
      const name = row?.user_name || '';
      if (name.toLowerCase().startsWith('sip:')) return { label: 'SIP', color: 'info', icon: <Dialpad fontSize="small" /> };
      if (name.toLowerCase().startsWith('tel:')) return { label: 'Tel', color: 'success', icon: <Phone fontSize="small" /> };
      return { label: 'App', color: 'primary', icon: <Apps fontSize="small" /> };
    };

    const stripIntercomPrefix = (name) => String(name || '').replace(/^(sip:|tel:|app:)/i, '');

    const renderEmptyState = (message) => (
      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="body2">{message}</Typography>
      </Box>
    );

    const renderActionButton = (title, icon, onClick, color = 'primary') => (
      <Tooltip title={title}>
        <IconButton color={color} size="small" onClick={onClick} sx={{ border: '1px solid', borderColor: 'divider' }}>
          {icon}
        </IconButton>
      </Tooltip>
    );

    const renderUserRow = (user) => {
      const type = getIntercomType(user);
      return (
        <Box
          key={user.intercom_user_id || user.user_name}
          sx={(t) => ({
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            gap: 1.5,
            alignItems: 'center',
            p: 1.5,
            border: `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            bgcolor: 'background.paper',
          })}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" color={type.color} icon={type.icon} label={type.label} />
              <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                {stripIntercomPrefix(user.user_name)}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {user.friendly_name || ETVConstants.trans('no_friendly_name') || 'No friendly name'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            {renderActionButton(ETVConstants.trans('edit_intercom_user') || 'Edit intercom user', <Edit fontSize="small" />, () => this.openEditIntercomUserDialog(user))}
            {renderActionButton(ETVConstants.trans('change_password') || 'Change password', <Password fontSize="small" />, () => this.openChangePasswordDialog(user), 'success')}
            {renderActionButton(ETVConstants.trans('delete') || 'Delete', <Delete fontSize="small" />, () => this.deleteIntercomUser(user.intercom_user_id), 'error')}
          </Stack>
        </Box>
      );
    };

    const renderDeviceRow = (device) => (
      <Box
        key={device.intercom_user_id || device.device_id || device.user_name}
        sx={(t) => ({
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          gap: 1.5,
          alignItems: 'center',
          p: 1.5,
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" color="primary" icon={<Devices fontSize="small" />} label={ETVConstants.trans('device') || 'Device'} />
            <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
              {stripIntercomPrefix(device.user_name)}
            </Typography>
            <TestCallStatusIcon
              datetime={device.testcall_datetime}
              result={device.testcall_result}
              reason={device.testcall_reason}
            />
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {(ETVConstants.trans('linked_device') || 'Linked device') + ': '} {device.device_name || device.device_id || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {(ETVConstants.trans('location') || 'Location') + ': '} {this.printAddress(device.location) || '-'}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
          {renderActionButton(ETVConstants.trans('edit_device_settings') || 'Edit device settings', <Edit fontSize="small" />, () => this.openEditDeviceDialog(device))}
          {renderActionButton(ETVConstants.trans('show_assignments') || 'Show assignments', <Assignment fontSize="small" />, () => this.showAssignDialog(device.device_id))}
          {renderActionButton(ETVConstants.trans('change_password') || 'Change password', <Password fontSize="small" />, () => this.openChangePasswordDialog(device), 'success')}
          {renderActionButton(ETVConstants.trans('delete') || 'Delete', <Delete fontSize="small" />, () => this.deleteIntercomUser(device.intercom_user_id), 'error')}
        </Stack>
      </Box>
    );
    return (
      <Box sx={{ width: 'min(1100px, 100%)' }}>
        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <CardHeader
            title={<Typography variant="h6">{ETVConstants.trans('intercom') || 'intercom'}</Typography>}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="medium" variant="contained" startIcon={<Dialpad />} onClick={this.openDTMFDialog} sx={{ minWidth: 150 }}>
                  {ETVConstants.trans('dtmf_commands')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<Devices />} onClick={this.openAddDeviceDialog} sx={{ minWidth: 190 }}>
                  {ETVConstants.trans('add_intercom_device') || 'Add intercom device'}
                </Button>
                <Button size="medium" variant="contained" startIcon={<PersonAdd />} onClick={this.openAddUserDialog} sx={{ minWidth: 170 }}>
                  {ETVConstants.trans('add_intercom_user') || 'Add intercom user'}
                </Button>
              </Box>
            }
            sx={(t) => ({
              px: 2,
              backgroundColor: t.palette.action.hover,
              borderBottom: `1px solid ${t.palette.divider}`,
              '& .MuiCardHeader-content': { display: 'flex', alignItems: 'center' },
            })}
          />

          {isLoading && <LinearProgress />}

          <CardContent sx={{ p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(ETVConstants.trans('error') || 'Error') + ': '}
                {String(error?.message || error)}
              </Alert>
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {ETVConstants.trans("devices")}
                </Typography>

                <Stack spacing={1.25}>
                  {(devices || []).length === 0
                    ? renderEmptyState(ETVConstants.trans('no_intercom_devices') || 'No intercom devices yet')
                    : devices.map(renderDeviceRow)}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {ETVConstants.trans("users")}
                </Typography>

                <Stack spacing={1.25}>
                  {(users || []).length === 0
                    ? renderEmptyState(ETVConstants.trans('no_intercom_users') || 'No intercom users yet')
                    : users.map(renderUserRow)}
                </Stack>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Info dialog */}
        <MessageDialog
          open={this.state.window === 'message_div'}
          spinning={this.state.spinning}
          onClose={() => this.closeInfoMessage()}
          title={this.state.info_title}
          message={this.state.info_message}
          type={this.state.info_type}
        />

        {/* Change password */}
        <Dialog
          open={window === 'change_password_div'}
          onClose={() => (this.state.changePwSaving ? null : this.showWindow('', false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{ETVConstants.trans('change_password') || 'Change password'}</DialogTitle>

          <DialogContentText sx={{ px: 3, pb: 1 }}>
            {this.state.changePwUserName}
          </DialogContentText>

          <Divider />

          <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
            <TextField
              label={ETVConstants.trans('new_password') || 'New password'}
              fullWidth
              type="password"
              value={this.state.changePwNew}
              onChange={(e) => this.setState({ changePwNew: e.target.value })}
              autoComplete="new-password"
            />

            <TextField
              label={ETVConstants.trans('confirm_password') || 'Confirm password'}
              fullWidth
              type="password"
              value={this.state.changePwConfirm}
              onChange={(e) => this.setState({ changePwConfirm: e.target.value })}
              autoComplete="new-password"
              error={!!this.state.changePwConfirm && this.state.changePwConfirm !== this.state.changePwNew}
              helperText={
                !!this.state.changePwConfirm && this.state.changePwConfirm !== this.state.changePwNew
                  ? ETVConstants.trans('passwords_do_not_match') || 'Passwords do not match'
                  : ' '
              }
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.showWindow('', false)} disabled={this.state.changePwSaving}>
              {ETVConstants.trans('close') || 'Close'}
            </Button>

            <Button startIcon={<Save />} variant="contained" disabled={!canSavePw} onClick={this.submitChangePassword}>
              {ETVConstants.trans('save') || 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit intercom user */}
        <Dialog
          open={window === 'edit_intercom_user_div'}
          onClose={() => (this.state.editUserSaving ? null : this.showWindow('', false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{ETVConstants.trans('edit_intercom_user') || 'Edit intercom user'}</DialogTitle>

          <DialogContentText sx={{ px: 3, pb: 1 }}>
            {this.state.editUserId ? `${this.state.editUserPrefix}${editUserNameValue}` : ''}
          </DialogContentText>

          <Divider />

          <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                {ETVConstants.trans('address_type') || 'Address type'}
              </FormLabel>
              <RadioGroup
                row
                value={editPrefix}
                onChange={(e) => this.setState({
                  editUserPrefix: e.target.value,
                  editUserName: '',
                  editUserPassword: '',
                  editUserSipExtension: '',
                  editUserSipDomain: '',
                  editUserSipPassword: '',
                  editUserSipTransport: 'udp',
                  showEditUserSipPassword: false,
                })}
              >
                <FormControlLabel value="sip:" control={<Radio />} label={`${ETVConstants.trans('sip_device') || 'SIP'} (sip:)`} />
                <FormControlLabel value="tel:" control={<Radio />} label={`${ETVConstants.trans('phone_number') || 'Tel'} (tel:)`} />
                <FormControlLabel value="app:" control={<Radio />} label={`${ETVConstants.trans('intercom_app') || 'App'} (app:)`} />
              </RadioGroup>
            </FormControl>

            {!editIsSip && (
              <TextField
                label={editIsTel ? (ETVConstants.trans('phone_number') || 'Phone number') : (ETVConstants.trans('app_user_name') || 'App username')}
                fullWidth
                value={this.state.editUserName}
                onChange={(e) => this.setState({ editUserName: e.target.value })}
                error={!!editUserNameError}
                helperText={editUserNameError || (editIsTel ? (ETVConstants.trans('user_name_tel_hint') || 'Enter a Twilio/E.164 phone number, e.g. +6591234567') : (ETVConstants.trans('user_name_hint') || 'Enter the app username without app:'))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{editPrefix}</InputAdornment>,
                }}
              />
            )}

            {editIsSip && (
              <>
                <TextField
                  label={ETVConstants.trans('sip_extension') || 'SIP extension'}
                  fullWidth
                  value={this.state.editUserSipExtension}
                  onChange={(e) => this.setState({ editUserSipExtension: e.target.value })}
                  error={!!editSipError && !editSipExtension}
                  helperText={editSipError && !editSipExtension ? editSipError : (ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this SIP user, e.g. 1003')}
                />
                <TextField
                  label={ETVConstants.trans('sip_domain') || 'SIP domain'}
                  fullWidth
                  value={this.state.editUserSipDomain}
                  onChange={(e) => this.setState({ editUserSipDomain: e.target.value })}
                  error={!!editSipError && !!editSipExtension && !editSipDomain}
                  helperText={editSipError && !!editSipExtension && !editSipDomain ? editSipError : (ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21')}
                />
                <FormControl fullWidth>
                  <InputLabel id="edit-user-sip-transport-label">
                    {ETVConstants.trans('sip_transport') || 'SIP transport'}
                  </InputLabel>
                  <Select
                    labelId="edit-user-sip-transport-label"
                    label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                    value={this.state.editUserSipTransport}
                    onChange={(e) => this.setState({ editUserSipTransport: e.target.value })}
                  >
                    <MenuItem value="udp">UDP</MenuItem>
                    <MenuItem value="tcp">TCP</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={ETVConstants.trans('sip_password') || 'SIP password'}
                  fullWidth
                  type={this.state.showEditUserSipPassword ? 'text' : 'password'}
                  value={this.state.editUserSipPassword}
                  onChange={(e) => this.setState({ editUserSipPassword: e.target.value })}
                  autoComplete="new-password"
                  error={!!editSipError && !!editSipExtension && !!editSipDomain && !editSipPassword}
                  helperText={editSipError && !!editSipExtension && !!editSipDomain && !editSipPassword ? editSipError : ' '}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => this.setState((prev) => ({ showEditUserSipPassword: !prev.showEditUserSipPassword }))}
                        >
                          {this.state.showEditUserSipPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <TextField
              label={ETVConstants.trans('friendly_name') || 'Friendly name'}
              fullWidth
              value={this.state.editFriendlyName}
              onChange={(e) => this.setState({ editFriendlyName: e.target.value })}
              helperText={ETVConstants.trans('friendly_name_hint') || 'Optional display name, e.g. Thomas, Reception, Security'}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.showWindow('', false)} disabled={this.state.editUserSaving}>
              {ETVConstants.trans('close') || 'Close'}
            </Button>
            <Button startIcon={<Save />} variant="contained" disabled={!canSaveEditUser} onClick={this.saveIntercomUserEdit}>
              {ETVConstants.trans('save') || 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* DTMF dialog */}
        <IntercomDTMFDialog
            open={window === 'dtmf_dialog_div'}
            onClose={() => this.setState({ window: null })}
        />

        {/* Add intercom user */}
        <Dialog
          open={window === 'add_intercom_user_div'}
          onClose={() => {}}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{addDialogTitle}</DialogTitle>

          <DialogContentText sx={{ px: 3, pb: 1 }}>
            {addDialogDesc}
          </DialogContentText>

          <Divider />

          <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
            {isAddingDevice && (
              <FormControl fullWidth>
                <InputLabel id="intercom-device-select-label">
                  {ETVConstants.trans('device') || 'Device'}
                </InputLabel>
                <Select
                  labelId="intercom-device-select-label"
                  id="intercom-device-select"
                  label={ETVConstants.trans('device') || 'Device'}
                  value={newDeviceId}
                  onChange={this.handleSelectDeviceForNewUser}
                >
                  {availableDevices.length === 0 && (
                    <MenuItem value="" disabled>
                      {ETVConstants.trans('no_available_devices') || 'No available devices'}
                    </MenuItem>
                  )}
                  {availableDevices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.device_name || d.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isAddingDevice && (
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">
                  {ETVConstants.trans('address_type') || 'Address type'}
                </FormLabel>
                <RadioGroup
                  row
                  value={effectivePrefix}
                  onChange={(e) => this.setState({
                    newUserPrefix: e.target.value,
                    newUserName: '',
                    newPassword: '',
                    newSipExtension: '',
                    newSipDomain: '',
                    newSipTransport: 'udp',
                    newSipTestExtension: '',
                    showNewSipPassword: false,
                  })}
                >
                  <FormControlLabel value="sip:" control={<Radio />} label={`${ETVConstants.trans('sip_device') || 'SIP'} (sip:)`} />
                  <FormControlLabel value="tel:" control={<Radio />} label={`${ETVConstants.trans('phone_number') || 'Tel'} (tel:)`} />
                  <FormControlLabel value="app:" control={<Radio />} label={`${ETVConstants.trans('intercom_app') || 'App'} (app:)`} />
                </RadioGroup>
              </FormControl>
            )}

            {!isSipUser && (
              <TextField
                label={
                  isAddingDevice
                    ? (ETVConstants.trans('app_name') || 'App name')
                    : isTelUser
                    ? (ETVConstants.trans('phone_number') || 'Phone number')
                    : (ETVConstants.trans('app_user_name') || 'App username')
                }
                fullWidth
                value={newUserName}
                onChange={(e) => this.setState({ newUserName: e.target.value })}
                error={!!userNameError}
                helperText={
                  userNameError ||
                  (
                    isAddingDevice
                      ? (ETVConstants.trans('app_name_hint') || 'Intercom app login name. It will be saved as app:name.')
                      : isTelUser
                      ? (ETVConstants.trans('user_name_tel_hint') || 'Enter a Twilio/E.164 phone number, e.g. +6591234567')
                      : (ETVConstants.trans('user_name_hint') || 'Enter the app username without app:')
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isAddingDevice ? 'app:' : effectivePrefix}
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {showSipFields && (
              <>
                <TextField
                  label={ETVConstants.trans('sip_extension') || 'SIP extension'}
                  fullWidth
                  value={this.state.newSipExtension}
                  onChange={(e) => this.setState({ newSipExtension: e.target.value })}
                  error={!!sipExtensionError}
                  helperText={sipExtensionError || (ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this device, e.g. 1002')}
                />
                <TextField
                  label={ETVConstants.trans('sip_domain') || 'SIP domain'}
                  fullWidth
                  value={this.state.newSipDomain}
                  onChange={(e) => this.setState({ newSipDomain: e.target.value })}
                  error={!!sipDomainError}
                  helperText={sipDomainError || (ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21')}
                />
                <FormControl fullWidth>
                  <InputLabel id="add-sip-transport-label">
                    {ETVConstants.trans('sip_transport') || 'SIP transport'}
                  </InputLabel>
                  <Select
                    labelId="add-sip-transport-label"
                    label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                    value={newSipTransport}
                    onChange={(e) => this.setState({ newSipTransport: e.target.value })}
                  >
                    <MenuItem value="udp">UDP</MenuItem>
                    <MenuItem value="tcp">TCP</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={ETVConstants.trans('sip_password') || 'SIP password'}
                  fullWidth
                  type={this.state.showNewSipPassword ? 'text' : 'password'}
                  value={this.state.newPassword}
                  onChange={(e) => this.setState({ newPassword: e.target.value })}
                  autoComplete="new-password"
                  error={!!passwordError}
                  helperText={passwordError || ' '}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => this.setState((prev) => ({ showNewSipPassword: !prev.showNewSipPassword }))}
                        >
                          {this.state.showNewSipPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {isAddingDevice && (
                  <TextField
                    label={ETVConstants.trans('sip_test_extension') || 'SIP test extension'}
                    fullWidth
                    value={this.state.newSipTestExtension}
                    onChange={(e) => this.setState({ newSipTestExtension: e.target.value })}
                    error={!!sipTestExtensionError}
                    helperText={sipTestExtensionError || (ETVConstants.trans('sip_test_extension_hint') || 'PBX extension for silent test calls, e.g. 9999')}
                  />
                )}
              </>
            )}

            {!isAddingDevice && (
              <>
                <TextField
                  label={ETVConstants.trans('friendly_name') || 'Friendly name'}
                  fullWidth
                  value={this.state.newFriendlyName}
                  onChange={(e) => this.setState({ newFriendlyName: e.target.value })}
                  helperText={
                    ETVConstants.trans('friendly_name_hint') ||
                    'Optional display name, e.g. Thomas, Reception, Security'
                  }
                />
                {showVisiblePassword && !isSipUser && (
                  <TextField
                    label={ETVConstants.trans('password') || 'Password'}
                    fullWidth
                    type="password"
                    value={this.state.newPassword}
                    onChange={(e) => this.setState({ newPassword: e.target.value })}
                    autoComplete="new-password"
                    error={!!passwordError}
                    helperText={passwordError || ' '}
                  />
                )}
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>
              {ETVConstants.trans('close') || 'Close'}
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              disabled={!canSave}
              onClick={() => this.saveIntercomUser()}
            >
              {ETVConstants.trans('save') || 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit device SIP settings */}
        <Dialog
          open={window === 'edit_intercom_device_div'}
          onClose={() => (this.state.editSipSaving ? null : this.showWindow('', false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{ETVConstants.trans('edit_device_settings') || 'Edit device settings'}</DialogTitle>

          <DialogContentText sx={{ px: 3, pb: 1 }}>
            {this.state.editDeviceName}
          </DialogContentText>

          <Divider />

          <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
            <TextField
              label={ETVConstants.trans('sip_extension') || 'SIP extension'}
              fullWidth
              value={this.state.editSipExtension}
              onChange={(e) => this.setState({ editSipExtension: e.target.value })}
              helperText={ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this device, e.g. 1002'}
            />
            <TextField
              label={ETVConstants.trans('sip_domain') || 'SIP domain'}
              fullWidth
              value={this.state.editSipDomain}
              onChange={(e) => this.setState({ editSipDomain: e.target.value })}
              helperText={ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21'}
            />
            <FormControl fullWidth>
              <InputLabel id="edit-sip-transport-label">
                {ETVConstants.trans('sip_transport') || 'SIP transport'}
              </InputLabel>
              <Select
                labelId="edit-sip-transport-label"
                label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                value={this.state.editSipTransport}
                onChange={(e) => this.setState({ editSipTransport: e.target.value })}
              >
                <MenuItem value="udp">UDP</MenuItem>
                <MenuItem value="tcp">TCP</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={ETVConstants.trans('sip_password') || 'SIP password'}
              fullWidth
              type={this.state.showEditSipPassword ? 'text' : 'password'}
              value={this.state.editSipPassword}
              onChange={(e) => this.setState({ editSipPassword: e.target.value })}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => this.setState((prev) => ({ showEditSipPassword: !prev.showEditSipPassword }))}
                    >
                      {this.state.showEditSipPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={ETVConstants.trans('sip_test_extension') || 'SIP test extension'}
              fullWidth
              value={this.state.editSipTestExtension}
              onChange={(e) => this.setState({ editSipTestExtension: e.target.value })}
              helperText={ETVConstants.trans('sip_test_extension_hint') || 'PBX extension for silent test calls, e.g. 9999'}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.showWindow('', false)} disabled={this.state.editSipSaving}>
              {ETVConstants.trans('close') || 'Close'}
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              disabled={this.state.editSipSaving}
              onClick={this.saveDeviceSipSettings}
            >
              {ETVConstants.trans('save') || 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Assignments for a device */}
        <Dialog open={window === 'assign_user_dialog_div'} onClose={() => {}} maxWidth="md" fullWidth>
          <DialogTitle>{ETVConstants.trans('emergency_user') || 'Emergency user'}</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
            {selected_device_id ? (
              <IntercomAssignmentDialog
                all_users={users}
                device_id={selected_device_id}
                onClose={() => {
                  this.setState({ window: '' });
                  this.loadIntercomUsers();
                }}
              />
            ) : (
              <span />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ window: '' })}>
              {ETVConstants.trans('close') || 'Close'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default IntercomMgmt;
